import { useCallback, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import type { QuestionnaireQuestionPayload } from "../../types";
import { questionnaireRespondService } from "../services/QuestionnaireRespondService";
import { toQuestionPreviewItems } from "../transformers/questionnaireRespondTransformers";
import type {
  QuestionnaireRespondFormValues,
  SubmitQuestionnaireResponseInput,
} from "../types";

export interface QuestionnaireRespondSubmitSuccessMeta {
  questionnaireTitle: string;
}

export interface UseQuestionnaireRespondParams {
  questionnaireId: string;
  subjectReference: string;
  /** Quando o fluxo veio de task com `basedOn` → Appointment. */
  appointmentReference?: string;
  /**
   * Patient do responsável que envia a resposta quando o formulário é do dependente
   * (`subjectReference` = dependente). Opcional para manter fluxos antigos inalterados.
   */
  authorReference?: string;
  /** Chamado após envio bem-sucedido (ex.: navegar para home do paciente). */
  onSubmitSuccess?: (meta: QuestionnaireRespondSubmitSuccessMeta) => void;
}

export interface UseQuestionnaireRespondResult {
  questionnaireTitle: string;
  questionnaireDescription: string;
  questions: QuestionnaireQuestionPayload[];
  previewItems: ReturnType<typeof toQuestionPreviewItems>;
  control: ReturnType<typeof useForm<QuestionnaireRespondFormValues>>["control"];
  controller: typeof Controller;
  isLoadingQuestionnaire: boolean;
  hasLoadError: boolean;
  isSubmittingResponse: boolean;
  hasSubmitError: boolean;
  hasQuestionnaire: boolean;
  handleSubmitResponse: () => void;
}

export function useQuestionnaireRespond(
  params: UseQuestionnaireRespondParams,
): UseQuestionnaireRespondResult {
  const {
    questionnaireId,
    subjectReference,
    appointmentReference,
    authorReference,
    onSubmitSuccess,
  } = params;

  const {
    control,
    handleSubmit,
    reset,
    setFocus,
  } = useForm<QuestionnaireRespondFormValues>({
    defaultValues: {
      answers: [],
    },
  });

  const {
    run: runLoadQuestionnaire,
    loading: isLoadingQuestionnaire,
    data: loadData,
    error: loadError,
  } = useAsyncTask<
    [string],
    Awaited<ReturnType<typeof questionnaireRespondService.loadQuestionnaireById>> | null
  >({
    task: questionnaireRespondService.loadQuestionnaireById,
  });

  const {
    run: runSubmitResponse,
    loading: isSubmittingResponse,
    error: submitError,
  } = useAsyncTask<[SubmitQuestionnaireResponseInput], fhir5.QuestionnaireResponse>({
    task: questionnaireRespondService.submitQuestionnaireResponse,
  });

  useEffect(() => {
    let mounted = true;

    runLoadQuestionnaire(questionnaireId)
      .then((result) => {
        if (!mounted || !result) return;
        reset(result.formValues);
      })
      .catch(() => {
        // useAsyncTask mantém o estado de erro para a view.
      });

    return () => {
      mounted = false;
    };
  }, [questionnaireId, reset, runLoadQuestionnaire]);

  const questionnaire = loadData?.questionnaire;
  const questions = questionnaire?.questions ?? [];
  const previewItems = useMemo(() => toQuestionPreviewItems(questions), [questions]);

  const scrollAndFocusField = useCallback(
    (fieldName: `answers.${number}.value`) => {
      setFocus(fieldName);

      const inputElement = document.querySelector<HTMLElement>(
        `[name="${fieldName}"]`,
      );
      inputElement?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    },
    [setFocus],
  );

  const handleSubmitResponse = handleSubmit(
    async (values) => {
      if (!questionnaire) return;

      try {
        await runSubmitResponse({
          questionnaireId,
          subjectReference,
          questionnaire,
          values,
          ...(appointmentReference ? { appointmentReference } : {}),
          ...(authorReference ? { authorReference } : {}),
        });
        toast.success("Resposta enviada ao prontuário.");
        onSubmitSuccess?.({
          questionnaireTitle: questionnaire?.title ?? "",
        });
      } catch {
        toast.error(
          "Não foi possível enviar a resposta. Verifique a rede e tente novamente.",
        );
      }
    },
    (formErrors) => {
      const firstInvalidQuestionIndex = questions.findIndex(
        (question, index) =>
          question.isRequired && Boolean(formErrors.answers?.[index]?.value),
      );

      if (firstInvalidQuestionIndex >= 0) {
        scrollAndFocusField(
          `answers.${firstInvalidQuestionIndex}.value` as `answers.${number}.value`,
        );
      }
    },
  );

  return {
    questionnaireTitle: questionnaire?.title ?? "",
    questionnaireDescription: questionnaire?.description ?? "",
    questions,
    previewItems,
    control,
    controller: Controller,
    isLoadingQuestionnaire,
    hasLoadError: Boolean(loadError),
    isSubmittingResponse,
    hasSubmitError: Boolean(submitError),
    hasQuestionnaire: Boolean(questionnaire),
    handleSubmitResponse,
  };
}
